import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, FileText } from 'lucide-react';
import { cn } from '../../utils/cn';
import { ThemeSelector } from '../../components/ThemeSelector';
import type { SiteTemplate } from '../../schemas/validation';

interface CreateSiteFormProps {
  newSiteName: string;
  selectedTheme: string;
  useTemplate: boolean;
  selectedTemplate: SiteTemplate | null;
  isCreating: boolean;
  createError: string | null;
  onNameChange: (name: string) => void;
  onThemeChange: (theme: string) => void;
  onUseTemplateChange: (use: boolean) => void;
  onShowTemplateLibrary: () => void;
  onSubmit: (e: React.FormEvent) => void;
  onCancel: () => void;
}

export const CreateSiteForm: React.FC<CreateSiteFormProps> = ({
  newSiteName,
  selectedTheme,
  useTemplate,
  selectedTemplate,
  isCreating,
  createError,
  onNameChange,
  onThemeChange,
  onUseTemplateChange,
  onShowTemplateLibrary,
  onSubmit,
  onCancel,
}) => {
  return (
    <div className="mb-8 animate-slide-up">
      <div className="card p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Create New Website</h3>

        {/* Template Selection Options */}
        <div className="mb-6">
          <div className="flex space-x-1 bg-gray-100 rounded-lg p-1">
            <button
              type="button"
              onClick={() => {
                onUseTemplateChange(false);
                onNameChange('');
              }}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                !useTemplate
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <Plus size={16} />
              <span>From Scratch</span>
            </button>
            <button
              type="button"
              onClick={onShowTemplateLibrary}
              className={cn(
                'px-4 py-2 rounded-md text-sm font-medium transition-colors flex items-center space-x-2',
                useTemplate
                  ? 'bg-white text-primary-600 shadow-sm'
                  : 'text-gray-600 hover:text-gray-900'
              )}
            >
              <FileText size={16} />
              <span>From Template</span>
            </button>
          </div>
        </div>

        {/* Selected Template Display */}
        {useTemplate && selectedTemplate && (
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Selected Template
            </label>
            <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">{selectedTemplate.name}</h4>
                  {selectedTemplate.description && (
                    <p className="text-sm text-gray-600 mt-1">{selectedTemplate.description}</p>
                  )}
                  <div className="flex items-center text-xs text-gray-500 mt-2">
                    <span>{selectedTemplate.siteData?.pages?.length || 0} pages</span>
                    {selectedTemplate.isPublic && (
                      <>
                        <span className="mx-2">•</span>
                        <span>Public template</span>
                      </>
                    )}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={onShowTemplateLibrary}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Change
                </button>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={onSubmit} className="space-y-4">
          <div>
            <label htmlFor="siteName" className="block text-sm font-medium text-gray-700 mb-2">
              Website Name
            </label>
            <input
              id="siteName"
              type="text"
              value={newSiteName}
              onChange={e => onNameChange(e.target.value)}
              placeholder="Enter website name"
              className="input-field w-full"
              required
              autoFocus
            />
          </div>

          {/* Theme Selection */}
          {!useTemplate && (
            <div className="border-t pt-4">
              <ThemeSelector selectedTheme={selectedTheme} onThemeSelect={onThemeChange} />
            </div>
          )}

          {/* Error message */}
          {createError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path
                      fillRule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clipRule="evenodd"
                    />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-800">{createError}</p>
                  {createError.toLowerCase().includes('maximum') && (
                    <div className="mt-2">
                      <Link
                        to="/pricing"
                        className="text-sm font-medium text-red-800 underline hover:text-red-900"
                      >
                        Upgrade your plan to create more websites →
                      </Link>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          <div className="flex space-x-4">
            <button
              type="submit"
              disabled={
                isCreating ||
                !newSiteName ||
                !newSiteName.trim() ||
                (useTemplate && !selectedTemplate)
              }
              className={cn(
                'btn-primary flex-1',
                (isCreating ||
                  !newSiteName ||
                  !newSiteName.trim() ||
                  (useTemplate && !selectedTemplate)) &&
                  'opacity-50 cursor-not-allowed'
              )}
            >
              {isCreating ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="w-4 h-4 spinner"></div>
                  <span>Creating...</span>
                </div>
              ) : useTemplate ? (
                'Create from Template'
              ) : (
                'Create Website'
              )}
            </button>
            <button type="button" onClick={onCancel} className="btn-secondary">
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
